/**
 * @author      Nikko Renolds
 * @package     Trap Tycoon
 * @link        https://github.com/Ni1kko/Trap-Tycoon
 * @copyright   (c) Fallox Developments2026.
 * @license     MIT
 * @description Public in-game release notes.
 */

import React, { useState } from "react";
import { CalendarDays, FileText, History } from "lucide-react";

const RELEASES = {
  current: [
    {
      version: "Beta v4.1.4",
      date: "5 September 2026",
      title: "Encrypted Pager hotfix",
      changes: [
        "Restored Pager as the dedicated area for private player-to-player messages and contacts.",
        "Fixed the profile Message button crashing the game when opening a direct conversation.",
        "Player profiles now open the selected person's Pager thread directly.",
        "Updated direct-message labels and controls to match the Pager theme.",
      ],
      note: "Existing conversations and messages are unchanged.",
    },
  ],
  previous: [
    {
      version: "Beta v4.1.3",
      date: "5 September 2026",
      title: "Profile badge stacking hotfix",
      changes: [
        "Grouped matching collectible badges into one compact profile stack with an exact owned count.",
        "Made badge rows wrap cleanly so large collections no longer stretch or break player profiles.",
        "Added themed hover and keyboard-focus details while keeping the Day 1 OG crest separate.",
        "Combined duplicate badge rows in public profile responses for smaller, more stable profile loads.",
        "Stopped repeat profile visits from incorrectly awarding additional veteran coins.",
      ],
      note: "No badges or player data were removed or reset; existing quantities are preserved.",
    },
    {
      version: "Beta v4.1.2",
      date: "5 September 2026",
      title: "Day One legacy update",
      changes: [
        "Awarded a permanent Day 1 OG profile badge to every human account created before the economy-reset cutoff.",
        "Added a gold animated OG crest that expands to reveal Day 1 OG when hovered or focused.",
        "Kept the OG entitlement separate from inventory so it survives the upcoming economy reset.",
        "Corrected yearly Service Medal progression and claim handling.",
        "Added a minimizable survival status panel with a saved display preference.",
        "Restored and updated the Field Manual and new-player guidance.",
        "Player information now refreshes automatically after scheduled engine updates.",
        "Improved React lifecycle reliability and hardened staff moderation actions.",
      ],
      note: "No economy or progression values were reset in this release.",
    },
    {
      version: "Beta v4.1.0",
      date: "21 July 2026",
      title: "Beta platform update",
      changes: [
        "Advanced Trap Tycoon to the current beta generation.",
        "Prepared the game for continued content and reliability updates.",
      ],
    },
    {
      version: "Beta v2.9",
      date: "Previous release",
      title: "Progression and operations",
      changes: [
        "Expanded progression, achievements, recognition, and Warehouse operations.",
        "Improved alliance requests, navigation, and general usability.",
      ],
    },
    {
      version: "Beta v2.8",
      date: "Previous release",
      title: "Combat and community",
      changes: [
        "Expanded combat, alliance, inventory, communication, and mobile features.",
        "Improved account linking and community integrations.",
      ],
    },
    {
      version: "Beta v2.7",
      date: "Previous release",
      title: "Alliances and territory",
      changes: [
        "Introduced alliances, territory control, player raids, and supporting systems.",
        "Added further reliability and account-management improvements.",
      ],
    },
    {
      version: "Beta v2.4",
      date: "Previous release",
      title: "Core gameplay expansion",
      changes: [
        "Expanded combat, inventory, products, scouting, and player guidance.",
        "Improved the main economy and progression interfaces.",
      ],
    },
    {
      version: "Beta v2.0 and earlier",
      date: "Legacy releases",
      title: "Game foundations",
      changes: [
        "Established the core account, progression, economy, social, and combat systems.",
      ],
    },
  ],
};

const TABS = [
  { id: "current", label: "Current Release" },
  { id: "previous", label: "Previous Releases" },
];

export default function Changelog() {
  const [activeSection, setActiveSection] = useState("current");

  return (
    <div className="flex min-h-[750px] flex-col border-2 border-zinc-800 bg-zinc-950 font-mono shadow-[0_0_50px_rgba(0,0,0,1)] md:flex-row">
      <div className="w-full space-y-2 border-b border-zinc-800 bg-black/80 p-4 md:w-64 md:border-b-0 md:border-r">
        <div className="mb-6 px-2">
          <h2 className="flex items-center gap-2 font-black uppercase italic text-white">
            <History className="text-blue-500" /> Release Notes
          </h2>
          <p className="text-[9px] uppercase tracking-tighter text-zinc-600">
            Public update history
          </p>
        </div>

        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSection(tab.id)}
            aria-pressed={activeSection === tab.id}
            className={`w-full border px-4 py-3 text-left text-[10px] font-black uppercase italic transition-all ${
              activeSection === tab.id
                ? "border-blue-400 bg-blue-600 text-black shadow-[0_0_20px_rgba(37,99,235,0.2)]"
                : "border-transparent text-zinc-500 hover:bg-zinc-900 hover:text-white"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="max-h-[750px] flex-1 space-y-5 overflow-y-auto bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-900/10 via-transparent to-transparent p-5 md:p-8">
        <div className="mb-8 flex items-center gap-4">
          <div className="border border-zinc-800 bg-zinc-900 p-3">
            <FileText className="text-blue-500" size={32} />
          </div>
          <div>
            <h3 className="text-3xl font-black uppercase italic text-white">
              {activeSection === "current"
                ? "Current Release"
                : "Previous Releases"}
            </h3>
            <p className="text-[10px] uppercase text-zinc-500">
              Player-facing changes and improvements
            </p>
          </div>
        </div>

        {RELEASES[activeSection].map((release) => (
          <article
            key={release.version}
            className="border border-zinc-800 bg-zinc-900/40 p-5"
          >
            <header className="mb-4 border-b border-zinc-800 pb-4">
              <div className="flex flex-col justify-between gap-2 md:flex-row md:items-start">
                <div>
                  <p className="text-sm font-black uppercase text-blue-400">
                    {release.version}
                  </p>
                  <h4 className="mt-1 text-lg font-black uppercase italic text-white">
                    {release.title}
                  </h4>
                </div>
                <p className="flex items-center gap-2 text-[10px] font-bold uppercase text-zinc-500">
                  <CalendarDays size={13} /> {release.date}
                </p>
              </div>
            </header>

            <ul className="space-y-3">
              {release.changes.map((change) => (
                <li
                  key={change}
                  className="flex gap-3 text-xs font-bold uppercase leading-relaxed text-zinc-400"
                >
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-blue-500" />
                  <span>{change}</span>
                </li>
              ))}
            </ul>

            {release.note && (
              <p className="mt-5 border-l-2 border-zinc-600 bg-black/40 px-4 py-3 text-[10px] font-bold uppercase leading-relaxed text-zinc-500">
                {release.note}
              </p>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
